import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {HTMLElement | null} */
  #returnFocusTarget = null;

  constructor() {
    super();

    // DialogComponent aguarda `animation.finished`. Navegadores podem pausar
    // essa promise quando a aba fica em segundo plano, deixando body e dialog
    // bloqueados. Finaliza somente a animação do drawer após uma margem segura;
    // o fluxo original continua responsável por close, cleanup e eventos.
    const animatedClose = this.closeDialog;
    this.closeDialog = async () => {
      const dialog = this.refs.dialog;
      const fallback = window.setTimeout(() => {
        if (!dialog?.open || !dialog.classList.contains('dialog-closing')) return;

        for (const animation of dialog.getAnimations({ subtree: false })) {
          try {
            animation.finish();
          } catch {
            animation.cancel();
          }
        }
      }, 500);

      try {
        await animatedClose();
      } finally {
        window.clearTimeout(fallback);
      }
    };
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#focusHeading);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#focusHeading);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !this.refs.dialog?.open) {
      this.#rememberFocus();
      this.showDialog();
    }

    this.#syncDrawerState(event);
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  #rememberFocus() {
    this.#returnFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  #focusHeading = () => {
    const heading = this.refs.dialog?.querySelector(
      this.refs.dialog.classList.contains('cart-drawer--empty')
        ? '#cart-drawer-heading-empty'
        : '#cart-drawer-heading'
    );
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
  };

  #restoreFocus = () => {
    const fallback = this.querySelector('[data-testid="cart-drawer-trigger"]');
    const target = this.#returnFocusTarget?.isConnected ? this.#returnFocusTarget : fallback;
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    this.#returnFocusTarget = null;
  };

  #syncDrawerState(event) {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    let isEmpty = event.detail.resource?.item_count === 0;
    if (event.detail.resource?.item_count === undefined) {
      const sectionHtml = Object.values(event.detail.data?.sections || {}).find(
        (html) => typeof html === 'string' && html.includes('cart-drawer-heading')
      );
      if (typeof sectionHtml === 'string') {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        isEmpty = Boolean(doc.querySelector('#cart-drawer-heading-empty'));
      } else {
        return;
      }
    }

    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#rememberFocus();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
